// SemanticUI-free pre-@plone/components
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { NavLink } from 'react-router-dom';
import doesNodeContainClick from '@kitconcept/volto-light-theme/helpers/doesNodeContainClick';
import { useIntl, defineMessages, injectIntl } from 'react-intl';
import cx from 'classnames';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';
import config from '@plone/volto/registry';

import { getNavigation } from '@plone/volto/actions/navigation/navigation';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import clearSVG from '@plone/volto/icons/clear.svg';
import NavItem from '@plone/volto/components/theme/Navigation/NavItem';

const messages = defineMessages({
  closeMenu: {
    id: 'Close menu',
    defaultMessage: 'Close menu',
  },
  openFatMenu: {
    id: 'Open menu',
    defaultMessage: 'Open menu',
  },
  openDropdown: {
    id: 'Open dropdown',
    defaultMessage: 'Open dropdown',
  },
});

// Inline styles for dropdown navigation
const dropdownStyles = `
  .dropdown-menu {
    display: flex;
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .dropdown-menu > li {
    position: relative;
    margin-right: 0.5rem;
  }

  .dropdown-menu > li.has-dropdown > .item {
    cursor: pointer;
    color: #FFF;
  }

  .dropdown-menu .dropdown-submenu {
    display: none;
    position: absolute;
    top: 75%;
    left: 0;
    background: #194A7A;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    min-width: 220px;
    z-index: 1000;
    padding: 0.5rem 0;
    border-radius: 4px;
    margin-top: 0.5rem;
  }

  .dropdown-menu > li.has-dropdown:hover > .dropdown-submenu {
    display: block;
  }

  .dropdown-submenu ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .dropdown-submenu > ul > li {
    position: relative;
  }

  .dropdown-submenu > ul > li:hover > .dropdown-sub-submenu {
    display: block;
  }

  .dropdown-submenu > ul > li > a {
    display: block;
    padding: 0.75rem 1.25rem;
    color: #FFF;
    text-decoration: none;
    transition: background-color 0.2s;
  }

  .dropdown-submenu > ul > li > a:hover,
  .dropdown-submenu > ul > li > a.current {
    text-decoration: underline;
  }

  .dropdown-sub-submenu {
    display: none;
    padding-left: 0;
    margin-left: 1.25rem;
    border-left: 2px solid #e0e0e0;
  }

  .dropdown-sub-submenu li {
    margin: 0;
  }

  .dropdown-sub-submenu a {
    display: block;
    padding: 0.5rem 1rem;
    color: #666;
    text-decoration: none;
    font-size: 0.9em;
    transition: background-color 0.2s;
  }

  .dropdown-sub-submenu a:hover,
  .dropdown-sub-submenu a.current {
    background-color: #f9f9f9;
    color: #333;
  }

  .dropdown-menu > li > .item {
    text-decoration: none;
    color: #000;
    font-weight: 500;
    padding: 0.75rem 0;
    display: inline-block;
    transition: color 0.2s;
  }

  .dropdown-menu > li > .item:hover,
  .dropdown-menu > li > .item.active {
    color: #FFF;
  }
`;

const Navigation = ({ pathname }) => {
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(null);
  const [currentOpenIndex, setCurrentOpenIndex] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState({});
  const navigation = useRef(null);
  const dispatch = useDispatch();
  const intl = useIntl();

  const headerSettings = useSelector(
    (state) =>
      state.content.data?.['@components']?.inherit?.['voltolighttheme.header']
        ?.data,
  );
  const formData = useSelector((state) => state.form.global);

  const has_fat_menu =
    !isEmpty(formData) && formData?.has_fat_menu
      ? formData.has_fat_menu
      : headerSettings?.has_fat_menu;

  const lang = useSelector((state) => state.intl.locale);
  const token = useSelector((state) => state.userSession.token, shallowEqual);
  const items = useSelector((state) => state.navigation.items, shallowEqual);

  // Inject styles for dropdown navigation
  useEffect(() => {
    if (!has_fat_menu) {
      const styleId = 'dropdown-navigation-styles';
      if (!document.getElementById(styleId)) {
        const styleElement = document.createElement('style');
        styleElement.id = styleId;
        styleElement.textContent = dropdownStyles;
        document.head.appendChild(styleElement);
      }
      return () => {
        const styleElement = document.getElementById(styleId);
        if (styleElement) {
          styleElement.remove();
        }
      };
    }
  }, [has_fat_menu]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navigation.current && doesNodeContainClick(navigation.current, e))
        return;
      closeMenu();
      setDropdownOpen({});
    };

    document.addEventListener('mousedown', handleClickOutside, false);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  }, []);

  useEffect(() => {
    if (!hasApiExpander('navigation', getBaseUrl(pathname))) {
      dispatch(getNavigation(getBaseUrl(pathname), config.settings.navDepth));
    }
  }, [pathname, token, dispatch]);

  const isActive = (url) => {
    return (url === '' && pathname === '/') || (url !== '' && pathname === url);
  };

  const openMenu = (index) => {
    if (index === currentOpenIndex) {
      setDesktopMenuOpen(null);
      setCurrentOpenIndex(null);
    } else {
      setDesktopMenuOpen(index);
      setCurrentOpenIndex(index);
    }
  };

  const closeMenu = () => {
    setDesktopMenuOpen(null);
    setCurrentOpenIndex(null);
  };

  const toggleDropdown = (index) => {
    setDropdownOpen((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        closeMenu();
        setDropdownOpen({});
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  return (
    <nav
      id="navigation"
      aria-label="navigation"
      className="navigation q container default"
      ref={navigation}
    >
      <div className={'computer large screen widescreen only'}>
        <ul className={cx('desktop-menu', { 'dropdown-menu': !has_fat_menu })}>
          {items.map((item, index) => (
            <li
              key={item.url}
              className={cx({
                'has-dropdown':
                  !has_fat_menu && item.items && item.items.length > 0,
              })}
            >
              {has_fat_menu ? (
                <>
                  <button
                    onMouseOver={() => openMenu(index)}
                    className={cx('item', {
                      active:
                        desktopMenuOpen === index ||
                        (!desktopMenuOpen && pathname === item.url),
                    })}
                    aria-label={intl.formatMessage(messages.openFatMenu)}
                    aria-expanded={desktopMenuOpen === index ? true : false}
                  >
                    <NavLink
                      to={item.url === '' ? '/' : item.url}
                      onClick={() => closeMenu()}
                    >
                      {item.title}
                    </NavLink>
                  </button>

                  <div className="submenu-wrapper">
                    <div
                      className={cx('submenu', {
                        active: desktopMenuOpen === index,
                      })}
                    >
                      <div className="submenu-inner">
                        <ul>
                          {item.items &&
                            item.items.length > 0 &&
                            item.items.map((subitem) => (
                              <li className="subitem-wrapper" key={subitem.url}>
                                <NavLink
                                  to={subitem.url}
                                  onClick={() => closeMenu()}
                                  className={cx({
                                    current: isActive(subitem.url),
                                  })}
                                >
                                  <span className="left-arrow">&#8212;</span>
                                  <span>
                                    {subitem.nav_title || subitem.title}
                                  </span>
                                </NavLink>
                                <div className="sub-submenu">
                                  <ul>
                                    {subitem.items &&
                                      subitem.items.length > 0 &&
                                      subitem.items.map((subsubitem) => (
                                        <li
                                          className="subsubitem-wrapper"
                                          key={subsubitem.url}
                                        >
                                          <NavLink
                                            to={subsubitem.url}
                                            onClick={() => closeMenu()}
                                            className={cx({
                                              current: isActive(subsubitem.url),
                                            })}
                                          >
                                            <span className="left-arrow">
                                              &#8212;
                                            </span>
                                            <span>
                                              {subsubitem.nav_title ||
                                                subsubitem.title}
                                            </span>
                                          </NavLink>
                                        </li>
                                      ))}
                                  </ul>
                                </div>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <NavLink
                    to={item.url === '' ? '/' : item.url}
                    className={cx('item', {
                      active: pathname === item.url,
                    })}
                  >
                    {item.title}
                  </NavLink>
                  {item.items && item.items.length > 0 && (
                    <div
                      className={cx('dropdown-submenu', {
                        open: dropdownOpen[index],
                      })}
                    >
                      <ul>
                        {item.items.map((subitem) => (
                          <li key={subitem.url}>
                            <NavLink
                              to={subitem.url}
                              className={cx({
                                current: isActive(subitem.url),
                              })}
                            >
                              {subitem.nav_title || subitem.title}
                            </NavLink>
                            {subitem.items && subitem.items.length > 0 && (
                              <ul className="dropdown-sub-submenu">
                                {subitem.items.map((subsubitem) => (
                                  <li key={subsubitem.url}>
                                    <NavLink
                                      to={subsubitem.url}
                                      className={cx({
                                        current: isActive(subsubitem.url),
                                      })}
                                    >
                                      {subsubitem.nav_title || subsubitem.title}
                                    </NavLink>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

Navigation.propTypes = {
  pathname: PropTypes.string.isRequired,
};

Navigation.defaultProps = {
  token: null,
};

export default injectIntl(Navigation);
